/** Quick 260713-kk0 — NotificationsApiService (NTF-07, NTF-03).
 *
 * Typed HttpClient wrapper for the in-app inbox endpoints (Task 3):
 *   GET  /api/me/notifications            → InboxResponse (items + unreadCount + total)
 *   POST /api/me/notifications/{id}/read  → 204 (mark one read)
 *   POST /api/me/notifications/read-all   → { updated }
 *
 * Mirrors NotificationPrefsApiService: inject HttpClient, base = environment.apiBase,
 * return typed Observables (HttpErrorResponse propagates to subscribers).
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/** One in-app notification row — matches the Task 3 NotificationInboxItemDto (camelCase). */
export interface InboxItem {
  id: string;
  eventType: string;
  subject: string;
  message: string;
  linkUrl: string;
  sentAtUtc: string;
  readAtUtc: string | null;
}

/** Inbox list envelope — matches NotificationInboxResponse. */
export interface InboxResponse {
  items: InboxItem[];
  unreadCount: number;
  total: number;
}

/** read-all result envelope. */
export interface MarkAllReadResult {
  updated: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationsApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBase;

  /** GET /api/me/notifications — caller's IN_APP rows newest-first + unreadCount. */
  list(unreadOnly?: boolean, top?: number, offset?: number): Observable<InboxResponse> {
    let params = new HttpParams();
    if (unreadOnly !== undefined) {
      params = params.set('unreadOnly', String(unreadOnly));
    }
    if (top !== undefined) {
      params = params.set('top', String(top));
    }
    if (offset !== undefined) {
      params = params.set('offset', String(offset));
    }
    return this.http.get<InboxResponse>(`${this.base}/api/me/notifications`, { params });
  }

  /** POST /api/me/notifications/{id}/read — mark a single notification read (idempotent). */
  markRead(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/api/me/notifications/${id}/read`, {});
  }

  /** POST /api/me/notifications/read-all — clear the badge; returns count updated. */
  markAllRead(): Observable<MarkAllReadResult> {
    return this.http.post<MarkAllReadResult>(`${this.base}/api/me/notifications/read-all`, {});
  }
}
